import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { biApi } from '../../api/bi'
import { colors, font, text } from '../../design/tokens'
import { Card, Cargando, ErrorMsg, Titulo } from './comunes'
import { fmtValor, tonoDe } from '../../components/reportes/formatoReporte'
import BarraPeriodo, { Chips } from '../../components/reportes/BarraPeriodo'

/**
 * BI → Reportes en el celular (Martin, 15 oct 2026: "quiero poder ver todo
 * desde el móvil"). Mismo documento que la compu; la tabla ancha se vuelve una
 * tarjeta por renglón con las columnas que el reporte marca para el celular
 * (`movil`: titulo / principal / secundaria).
 */
export default function MovilReportes() {
  const { clave } = useParams()
  return clave ? <Reporte clave={clave} /> : <Catalogo />
}

function Catalogo() {
  const navigate = useNavigate()
  const [lista, setLista] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => { biApi.reportes().then(({ data }) => setLista(data)).catch(() => setError('No se pudo leer la lista de reportes.')) }, [])
  if (error) return <ErrorMsg>{error}</ErrorMsg>
  if (!lista) return <Cargando />
  return (
    <div>
      <Titulo>Reportes</Titulo>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {lista.map(r => (
          <Card key={r.clave} onClick={() => navigate(`/m/reportes/${r.clave}`)}>
            <div style={{ fontWeight: 600, fontSize: 16, color: colors.text, fontFamily: font.sans }}>{r.titulo}</div>
            <div style={{ fontSize: 12.5, color: colors.textSec, marginTop: 2 }}>{r.descripcion}</div>
          </Card>
        ))}
      </div>
    </div>
  )
}

function Reporte({ clave }) {
  const [params, setParams] = useState({})
  const [r, setR] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const ultimoRef = useRef(null)

  const cargar = useCallback(async (p) => {
    setCargando(true); setError(null)
    try { const { data } = await biApi.reporte(clave, p); setR(data) } catch { setError('No se pudo armar el reporte.') } finally { setCargando(false) }
  }, [clave])
  // Carga inicial: el estado se toca solo al llegar la respuesta.
  useEffect(() => {
    let vivo = true
    biApi.reporte(clave, {})
      .then(({ data }) => { if (vivo) { setR(data); setError(null) } })
      .catch((e) => { if (vivo) setError(e && 'No se pudo armar el reporte.') })
      .finally(() => { if (vivo) setCargando(false) })
    return () => { vivo = false }
  }, [clave])

  if (error) return <ErrorMsg>{error}</ErrorMsg>
  if (!r) return <Cargando />

  const cols = r.tabla.columnas
  const colTitulo = cols.find(c => c.movil === 'titulo')
  const principales = cols.filter(c => c.movil === 'principal')
  const secundarias = cols.filter(c => c.movil === 'secundaria')
  const diaAct = cols.filter(c => c.formato === 'dia').slice(-1)[0]
  // El último renglón con dato del año: hasta ahí llega la temporada.
  const filas = r.tabla.filas
  const idxUltimo = filas.reduce((ult, f, i) => (!f.separador && principales.some(c => f[c.clave] != null) ? i : ult), -1)
  const anio = r.parametros.find(p => p.tipo === 'anio')
  // Una tabla de días (fecha en la tarjeta) o de indicadores (nombre largo).
  const esFecha = colTitulo?.clave === 'fecha'
  const periodo = r.parametros.find(p => p.tipo === 'periodo')
  const chips = r.parametros.filter(p => p.tipo === 'opcion' || p.tipo === 'multi')
  const aplicar = (patch) => {
    const p = Object.fromEntries(Object.entries({ ...params, ...patch }).filter(([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)))
    setParams(p); cargar(p)
  }
  const nFiltros = chips.reduce((n, c) => n + (Array.isArray(c.valor) ? c.valor.length : (c.valor ? 1 : 0)), 0)

  return (
    <div style={{ opacity: cargando ? 0.6 : 1 }}>
      <Titulo>{r.titulo}</Titulo>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ fontSize: 13, color: colors.textSec }}>{r.descripcion}</span>
        {anio && (
          <select value={anio.valor} onChange={e => { const p = { ...params, anio: e.target.value, corte: undefined }; setParams(p); cargar(p) }}
            style={{ marginLeft: 'auto', border: `1px solid ${colors.border}`, borderRadius: 10, padding: '8px 10px', fontSize: 16, fontFamily: font.sans, background: colors.bg, color: colors.text }}
            aria-label="Año">
            {anio.opciones.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
      </div>

      {periodo && (
        <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 12, marginBottom: 10 }}>
          <BarraPeriodo p={periodo} onCambiar={aplicar} grande />
        </div>
      )}
      {chips.length > 0 && (
        <details style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 14, padding: '10px 12px', marginBottom: 10 }}>
          <summary style={{ fontWeight: 600, fontSize: 14, color: colors.text, fontFamily: font.sans }}>Filtros{nFiltros ? ` (${nFiltros})` : ''}</summary>
          <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
            {chips.map(c => <Chips key={c.clave} p={c} onCambiar={aplicar} grande />)}
          </div>
        </details>
      )}

      {r.avisos.map((a, i) => (
        <div key={i} style={{ background: colors.warnBg, color: colors.warn, border: `1px solid ${colors.warnBorder}`, borderRadius: 12, padding: '10px 12px', fontSize: 13, marginBottom: 8 }}>{a}</div>
      ))}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '6px 0 12px' }}>
        {r.kpis.map((k, i) => (
          <Card key={k.label} style={{ padding: '10px 12px', ...(i === r.kpis.length - 1 && r.kpis.length % 2 ? { gridColumn: '1 / -1' } : {}) }}>
            <div style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 17, fontWeight: 600, color: k.tono === 'neg' ? colors.err : k.tono === 'pos' ? colors.ok : colors.text }}>
              {fmtValor(k.valor, k.formato, { corto: true })}
            </div>
            <div style={{ fontSize: 12, color: colors.textSec, marginTop: 2, lineHeight: 1.3 }}>{k.label}</div>
            {k.nota && <div style={{ fontSize: 11, color: colors.textMeta, marginTop: 2 }}>{k.nota}</div>}
          </Card>
        ))}
      </div>

      {esFecha && idxUltimo >= 0 && (
        <button type="button" onClick={() => ultimoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
          style={{ width: '100%', border: `1px solid ${colors.border}`, background: colors.bg, borderRadius: 12, padding: '10px', fontSize: 14, fontFamily: font.sans, fontWeight: 600, color: colors.link, marginBottom: 10 }}>
          Ir al último día con datos ↓
        </button>
      )}

      {/* Qué es cada cifra de la tarjeta: fija arriba mientras se recorren los días. */}
      <div style={{ position: 'sticky', top: 52, zIndex: 2, background: colors.bgSoft, display: 'flex', alignItems: 'center', padding: '6px 12px', margin: '0 0 6px', borderRadius: 10, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textSec }}>
        <span>{colTitulo?.label || ''}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {principales.map(c => <span key={c.clave} style={{ ...colMovil(c), textAlign: 'right' }}>{c.corto || c.label}</span>)}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {filas.map((f, i) => f.separador ? (
          <div key={'s' + i} style={{ fontSize: text.meta, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: colors.textSec, margin: '10px 2px 2px' }}>{f.separador}</div>
        ) : (
          <div key={i} ref={i === idxUltimo ? ultimoRef : undefined}>
            <Card style={{ padding: '9px 12px', ...(f.sub ? { marginLeft: 18, background: colors.bgSoft } : {}), ...(i === idxUltimo ? { borderColor: colors.link, background: colors.linkBg } : {}) }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={esFecha
                  ? { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 600, fontSize: 14, color: colors.text, whiteSpace: 'nowrap' }
                  : { fontFamily: font.sans, fontWeight: 600, fontSize: 14, color: colors.text, flex: 1, minWidth: 0 }}>{f[colTitulo?.clave]}</span>
                {diaAct && <span style={{ fontSize: 12, color: colors.textMeta }}>{f[diaAct.clave]}</span>}
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 13, whiteSpace: 'nowrap' }}>
                  {principales.map(c => {
                    const tono = tonoDe(f[c.clave], c.formato)
                    return <span key={c.clave} title={c.label} style={{ ...colMovil(c), textAlign: 'right', color: tono === 'neg' ? colors.err : tono === 'pos' ? colors.ok : colors.text, fontWeight: c.formato === 'pesos' ? 600 : 500 }}>{fmtValor(f[c.clave], c.formato, { corto: true })}</span>
                  })}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 3, fontSize: 12, color: colors.textSec, flexWrap: 'wrap' }}>
                {secundarias.map(c => c.formato === 'texto'
                  ? (f[c.clave] ? <span key={c.clave}>{f[c.clave]}</span> : null)
                  : <span key={c.clave}>{c.label} <span style={{ fontFamily: font.mono }}>{fmtValor(f[c.clave], c.formato, { corto: true })}</span></span>)}
              </div>
            </Card>
          </div>
        ))}
      </div>


      {r.secciones.map(s => (
        <details key={s.titulo} style={{ marginTop: 12, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 14, padding: '10px 14px' }}>
          <summary style={{ fontWeight: 600, fontSize: 14, color: colors.text }}>{s.titulo}</summary>
          {s.items.map(it => (
            <div key={it.t} style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: colors.text }}>{it.t}</div>
              <div style={{ fontSize: 13, color: colors.textSec }}>{it.d}</div>
            </div>
          ))}
        </details>
      ))}
    </div>
  )
}

/** Ancho fijo por columna en la tarjeta: las cifras quedan alineadas entre días y no se parten. */
const colMovil = c => ({ minWidth: c.formato === 'pesos' ? 74 : 60, flexShrink: 0, whiteSpace: 'nowrap' })
